<template>
  <Drawer
    id="project-sidebar"
    :block-scroll="true"
    :modal="false"
    :pt="{
      root: '!w-[32rem]',
      content: 'p-0 h-full w-full overflow-hidden',
    }"
    :visible="show_project_sidebar"
    data-test="employee-project-sidebar"
    @update:visible="$emit('closeProjectSidebar')"
  >
    <template #header>
      <span class="text-2xl font-semibold">
        {{ $t('navbar.employee.projects') }}
      </span>
    </template>
    <div class="flex h-full flex-col">
      <div class="px-5">
        <SelectProject
          :autofocus="true"
          :is_dropdown="false"
          :label="$t('project.labels.searchProject')"
          @changeSelect="(q) => (search_project = q.attributes.name)"
          @onSearch="(q) => (search_project = q)"
        />
      </div>
      <div class="mx-4 mb-2 mt-5">
        <div
          v-if="show_all_projects"
          class="group flex cursor-pointer items-center rounded-md p-3 text-surface-600 transition-colors duration-200 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-700"
          @click="goToOverview"
        >
          <div class="grow truncate text-surface-700 dark:text-surface-0/80">
            {{ $t('navbar.employee.all_projects') }}
          </div>
        </div>
      </div>

      <div class="mb-4 overflow-auto p-1 px-3">
        <ProjectSection
          v-for="section in projectSections"
          :key="section.title"
          v-bind="section.props"
          v-on="section.events"
        />
      </div>
      <div
        v-if="current_profile.attributes.role === Employee.ROLE_OWNER"
        class="mt-auto shrink-0"
      >
        <div class="mx-4 mb-2">
          <ButtonPrimary
            action="create"
            class="mb-3 w-full"
            data-test="create-project-new"
            @onClick="goToProjectCreate"
          >
            {{ $t('project.labels.project') }}
          </ButtonPrimary>
        </div>
      </div>
    </div>
  </Drawer>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { ROUTES_EMPLOYEE_OVERVIEWS } from '@/app/data/route_constants';
import { Assignment, Employee, LEVEL_TYPE, Project } from '@/app/data/model_constants';
import TheProjectModal from '@/app/_employees/components/projects/form/TheProjectModal.vue';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import ProjectSection from './ProjectSection.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const { proxy: app } = getCurrentInstance();

const props = defineProps({
  show_project_sidebar: Boolean,
});
const emit = defineEmits(['closeProjectSidebar']);

const router = useRouter();
const store = useStore();

const assignments = ref([]);
const loaded_other_projects = ref(false);
const loaded_project_templates = ref(false);
const search_project = ref('');
const toggled_project_id = ref(null);
const toggle_projects_follow = ref(true);
const toggle_projects_other = ref(false);
const toggle_projects_template = ref(false);
const is_fetching_other = ref(false);
const is_fetching_template = ref(false);

const projects_all = computed(() => store.getters.projects);
const current_profile = computed(() => store.getters.current_profile);

function filterProjects(projects, filterFn) {
  const projectArray = Array.isArray(projects) ? projects : Object.values(projects);

  const search = search_project.value?.toLowerCase();

  return projectArray
    .filter((project) => {
      if (!filterFn(project)) return false;
      if (search) {
        const name = project.attributes.name?.toLowerCase() || '';
        return name.includes(search);
      }
      return true;
    })
    .sort((a, b) => (a.attributes.name || '').localeCompare(b.attributes.name || ''));
}

const projects_follow = computed(() =>
  filterProjects(
    _.pick(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) => project.attributes.status === Project.STATUS_ENABLED && !project.attributes.skip_customer,
  ),
);

const projects_other = computed(() =>
  filterProjects(
    _.omit(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) => project.attributes.status === Project.STATUS_ENABLED && !project.attributes.skip_customer,
  ),
);

const projects_template = computed(() =>
  filterProjects(projects_all.value, (project) => project.attributes.skip_customer),
);

const show_all_projects = computed(
  () =>
    app.$flipper.enabled(app.$FT.TODOS) ||
    app.$flipper.enabled(app.$FT.DOCUMENTS) ||
    app.$flipper.enabled(app.$FT.DECISIONS_CORE),
);

watch(search_project, () => {
  if (!search_project.value) return;
  toggle_projects_other.value = _.size(projects_other.value) > 0;
  toggle_projects_follow.value = _.size(projects_follow.value) > 0;
  toggle_projects_template.value = _.size(projects_template.value) > 0;
});

watch(
  () => props.show_project_sidebar,
  async (val) => {
    if (val) {
      search_project.value = '';
      assignments.value = await store.dispatch('FETCH_ASSIGNMENTS', {
        assigned_type: LEVEL_TYPE.PROJECT,
        assignee_type: current_profile.value.type,
        assignee_id: current_profile.value.id,
        assignment_type: Assignment.TYPE_FOLLOW,
      });
    }
  },
  { immediate: true },
);

async function createAssignment(assignment) {
  const assignment_new = await store.dispatch('CREATE_ASSIGNMENT', { record: assignment });
  assignments.value.push(assignment_new);
}

function toggleProject(project_id) {
  toggled_project_id.value = project_id;
}

async function deleteAssignment(assignment) {
  await store.dispatch('DELETE_ASSIGNMENT', { record: assignment });
  assignments.value = assignments.value.filter((a) => a.id !== assignment.id);
}

async function goToProjectCreate() {
  if (app.$flipper.enabled(app.$FT.TEMPLATE_PROJECTS) && !loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  emit('closeProjectSidebar');
  app.$buefy.modal.open({
    component: TheProjectModal,
  });
}

async function toggleOtherProjects() {
  is_fetching_other.value = true;
  toggle_projects_other.value = !toggle_projects_other.value;
  if (!loaded_other_projects.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: false, followed: false });
    loaded_other_projects.value = true;
  }
  is_fetching_other.value = false;
}

async function toggleProjectTemplates() {
  is_fetching_template.value = true;
  toggle_projects_template.value = !toggle_projects_template.value;
  if (!loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  is_fetching_template.value = false;
}

const projectSections = computed(() => [
  {
    title: t('project.labels.followedProjects'),
    props: {
      title: t('project.labels.followedProjects'),
      is_open: toggle_projects_follow.value,
      on_toggle: () => {
        toggle_projects_follow.value = !toggle_projects_follow.value;
      },
      projects: projects_follow.value,
      item_props: { assignments: assignments.value, is_following_project: true },
      toggled_project_id: toggled_project_id.value,
    },
    events: {
      closeProjectSidebar: () => emit('closeProjectSidebar'),
      toggleProjectId: toggleProject,
      unfollow: (assignment) => deleteAssignment(assignment),
    },
  },
  {
    title: t('project.labels.otherProjects'),
    props: {
      title: t('project.labels.otherProjects'),
      is_open: toggle_projects_other.value,
      is_fetching: is_fetching_other.value,
      on_toggle: toggleOtherProjects,
      projects: projects_other.value,
      item_props: { assignments: assignments.value },
      toggled_project_id: toggled_project_id.value,
    },
    events: {
      closeProjectSidebar: () => emit('closeProjectSidebar'),
      toggleProjectId: toggleProject,
      follow: (assignment) => createAssignment(assignment),
    },
  },
  {
    title: t('project.labels.templateProjects'),
    props: {
      title: t('project.labels.templateProjects'),
      is_open: toggle_projects_template.value,
      is_fetching: is_fetching_template.value,
      on_toggle: toggleProjectTemplates,
      projects: projects_template.value,
      item_props: { assignments: assignments.value, is_template: true },
      toggled_project_id: toggled_project_id.value,
    },
    events: {
      closeProjectSidebar: () => emit('closeProjectSidebar'),
      toggleProjectId: toggleProject,
    },
  },
]);
async function goToOverview() {
  emit('closeProjectSidebar');
  if (app.$flipper.enabled(app.$FT.TODOS)) {
    await router.push({ name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASKS });
  } else if (app.$flipper.enabled(app.$FT.DOCUMENTS)) {
    await router.push({ name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DOCUMENTS });
  } else if (app.$flipper.enabled(app.$FT.DECISIONS_CORE)) {
    await router.push({ name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DECISIONS });
  }
}
</script>
